import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format, parseISO, addHours } from "date-fns";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

import { Button } from '../ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Calendar } from '../ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { CalendarIcon, User, Phone, Mail, MapPin } from "lucide-react";
import { cn } from '../../lib/utils';
import { TimePicker } from '../ui/time-picker';
import { AddressInput } from '../ui/address-input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useEvents, EventInput } from '../../hooks/use-events';
import { apiRequest } from '../../lib/queryClient';
import { useToast } from '../../hooks/use-toast';
import { AlertCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { createEventDateTime, extractEventDateTime, formatDate, formatTime } from '../../lib/date-utils';
import { formatPhone } from '../../lib/validation';

// Validation schema for the form - complete for creating/editing events
const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional(),
  date: z.date(),
  startTime: z.string().min(1, "Please select a start time"),
  endTime: z.string().min(1, "Please select an end time"),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),
  type: z.enum(["estimate", "meeting", "site-visit", "delivery", "invoice", "appointment", "other"]),
  status: z.enum(["pending", "confirmed", "completed", "cancelled", "scheduled"]),
  clientId: z.string().optional(),
  projectId: z.string().optional(),
  agentId: z.string().optional(),
  notes: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

// Interfaz para las propiedades del componente
interface EventFormProps {
  eventId?: number;
  defaultClientId?: string;
  defaultProjectId?: string;
  defaultType?: string;
  defaultDate?: Date | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export default function EventForm({
  eventId,
  defaultClientId,
  defaultProjectId,
  defaultType = "estimate",
  defaultDate,
  onSuccess,
  onCancel,
}: EventFormProps) {
  const [date, setDate] = useState<Date | undefined>(defaultDate ?? new Date());
  const [formLoaded, setFormLoaded] = useState(false);
  const { toast } = useToast();
  
  console.log("🎯 EventForm received eventId:", eventId, "type:", typeof eventId);
  
  const { getEvent, createEventMutation, updateEventMutation, deleteEventMutation } = useEvents();
  
  // Query the event if editing an existing one
  const { data: eventData, isLoading: isLoadingEvent } = useQuery({
    queryKey: ["/api/protected/events/single", eventId],
    queryFn: async () => {
      if (!eventId) return null;
      console.log("🔍 Fetching event data for ID:", eventId);
      const response = await apiRequest("GET", `/api/protected/events?id=${eventId}`);
      const data = await response.json();
      console.log("📥 Received event data:", data);
      console.log("📥 Data type:", typeof data, Array.isArray(data));
      console.log("📥 Data length:", data?.length);
      return data;
    },
    enabled: !!eventId,
  });
  
  // Query clients
  const { data: clients = [], isLoading: isLoadingClients } = useQuery<any[]>({
    queryKey: ["/api/protected/clients"],
    queryFn: async () => {
      console.log("👥 Fetching clients...");
      const response = await apiRequest("GET", "/api/protected/clients");
      const data = await response.json();
      console.log("✅ Received clients:", data.length, "clients");
      return data;
    },
  });

  // Query projects
  const { data: projects = [], isLoading: isLoadingProjects } = useQuery<any[]>({
    queryKey: ["/api/protected/projects"],
    queryFn: async () => {
      console.log("📋 Fetching projects...");
      const response = await apiRequest("GET", "/api/protected/projects");
      const data = await response.json();
      console.log("✅ Received projects:", data.length, "projects");
      return data;
    },
  });

  // Query agents
  const { data: agents = [], isLoading: isLoadingAgents } = useQuery<any[]>({
    queryKey: ["/api/protected/agents"],
    queryFn: async () => {
      console.log("👥 Fetching agents...");
      const response = await apiRequest("GET", "/api/protected/agents");
      const data = await response.json();
      console.log("✅ Received agents:", data.length, "agents");
      return data;
    },
  });

  // Agent colors from database
  const getAgentColor = (agentId: number) => {
    const agent = agents.find(a => a.id === agentId);
    return agent?.colorCode || '#3B82F6';
  };

  // Filter projects by selected client
  const getFilteredProjects = (clientId: string) => {
    if (!clientId) return [];
    return (projects as any[]).filter((project: any) => project.clientId === Number(clientId));
  };

  // Configure the form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      date: defaultDate ?? new Date(),
      startTime: format(new Date(), "h:mm a"),
      endTime: format(addHours(new Date(), 1), "h:mm a"),
      address: "",
      city: "",
      state: "",
      zip: "",
      type: (defaultType as "estimate" | "meeting" | "site-visit" | "delivery" | "invoice" | "appointment" | "other") || "estimate",
      status: "pending",
      clientId: defaultClientId || "",
      projectId: defaultProjectId || "",
      agentId: "",
      notes: "",
    },
  });

  // Update form values when an existing event is loaded
  useEffect(() => {
    if (eventData && !isLoadingEvent) {
      console.log("🔄 Loading event data for form:", eventData);
      console.log("📋 Available clients:", clients);
      console.log("👥 Available agents:", agents);
      
      try {
        // Prefer ISO strings (startDate/endDate) for reliable parsing; fallback to timestamps
        const startStr = eventData.startDate ?? (eventData.startTime != null ? new Date(eventData.startTime).toISOString() : null);
        const endStr = eventData.endDate ?? (eventData.endTime != null ? new Date(eventData.endTime).toISOString() : null);
        if (!startStr || !endStr) {
          throw new Error("Event missing start/end date");
        }
        const { date, startTimeStr, endTimeStr } = extractEventDateTime(startStr, endStr);
        const formValues = {
          title: eventData.title || "",
          description: eventData.description || "",
          date: date,
          startTime: startTimeStr,
          endTime: endTimeStr,
          address: eventData.address || "",
          city: eventData.city || "",
          state: eventData.state || "",
          zip: eventData.zip || "",
          type: eventData.type || "estimate",
          status: eventData.status || "pending",
          clientId: eventData.clientId ? String(eventData.clientId) : "",
          projectId: eventData.projectId ? String(eventData.projectId) : "",
          agentId: eventData.agentId ? String(eventData.agentId) : "",
          notes: eventData.notes || "",
        };
        
        console.log("✅ Setting form values:", formValues);
        console.log("🔍 Form values breakdown:");
        console.log("  - Title:", formValues.title);
        console.log("  - Description:", formValues.description);
        console.log("  - Date:", formValues.date);
        console.log("  - Start Time:", formValues.startTime);
        console.log("  - End Time:", formValues.endTime);
        console.log("  - Address:", formValues.address);
        console.log("  - City:", formValues.city);
        console.log("  - State:", formValues.state);
        console.log("  - Zip:", formValues.zip);
        console.log("  - Type:", formValues.type);
        console.log("  - Status:", formValues.status);
        console.log("  - Client ID:", formValues.clientId);
        console.log("  - Project ID:", formValues.projectId);
        console.log("  - Agent ID:", formValues.agentId);
        console.log("  - Notes:", formValues.notes);
        
        // Reset form with new values
        form.reset(formValues);
        setDate(date);
        
        // Force form to update by setting values individually
        setTimeout(() => {
          console.log("🔧 Setting title:", formValues.title);
          form.setValue("title", formValues.title);
          form.setValue("description", formValues.description);
          form.setValue("date", formValues.date);
          form.setValue("startTime", formValues.startTime);
          form.setValue("endTime", formValues.endTime);
          form.setValue("address", formValues.address);
          form.setValue("city", formValues.city);
          form.setValue("state", formValues.state);
          form.setValue("zip", formValues.zip);
          form.setValue("type", formValues.type);
          form.setValue("status", formValues.status);
          form.setValue("clientId", formValues.clientId);
          form.setValue("projectId", formValues.projectId);
          form.setValue("agentId", formValues.agentId);
          form.setValue("notes", formValues.notes);
          
          // Debug: Check form values after setting
          const currentValues = form.getValues();
          console.log("🔍 Form values after setValue:", currentValues);
          
          // Verify the form was actually updated
          console.log("✅ Form successfully populated with event data");
          setFormLoaded(true);
        }, 100);
        
      } catch (error) {
        console.error("❌ Error parsing event dates:", error);
        // Fallback to default values if date parsing fails
        const fallbackValues = {
          title: eventData.title || "",
          description: eventData.description || "",
          date: new Date(),
          startTime: "10:00 AM",
          endTime: "11:00 AM",
          address: eventData.address || "",
          city: eventData.city || "",
          state: eventData.state || "",
          zip: eventData.zip || "",
          type: eventData.type || "estimate",
          status: eventData.status || "pending",
          clientId: eventData.clientId ? String(eventData.clientId) : "",
          projectId: eventData.projectId ? String(eventData.projectId) : "",
          agentId: eventData.agentId ? String(eventData.agentId) : "",
          notes: eventData.notes || "",
        };
        
        console.log("🔄 Using fallback values:", fallbackValues);
        form.reset(fallbackValues);
        setDate(new Date());
      }
    } else if (eventData && !isLoadingEvent) {
      console.log("⚠️ Event data loaded but missing required fields:", eventData);
      console.log("  - Has startTime:", !!eventData.startTime);
      console.log("  - Has endTime:", !!eventData.endTime);
    }
  }, [eventData, isLoadingEvent, form, clients, agents]);

  // Watch changes in clientId to update projects
  const watchClientId = form.watch("clientId");
  const filteredProjects = getFilteredProjects(watchClientId || "");

  // Get the current event from the events array (defined early to avoid temporal dead zone)
  const eventIdNum = eventId ? (typeof eventId === 'string' ? parseInt(eventId) : eventId) : 0;
  const currentEvent = eventData && Array.isArray(eventData) 
    ? eventData.find((event: any) => event.id === eventIdNum)
    : (eventData && !Array.isArray(eventData) ? eventData : null);

  // Get the selected client's information for display
  const selectedClient = currentEvent?.clientId 
    ? clients.find((client: any) => client.id === currentEvent.clientId)
    : null;

  // Get selected client for display (works for both new and editing events)
  const selectedClientForDisplay = watchClientId && watchClientId !== "none" && watchClientId !== "new_client"
    ? clients.find((client: any) => client.id === Number(watchClientId))
    : (eventId && selectedClient ? selectedClient : null);

  // Auto-fill address when client is selected
  useEffect(() => {
    if (watchClientId && watchClientId !== "none" && watchClientId !== "new_client") {
      const selectedClient = clients.find(client => client.id === Number(watchClientId));
      if (selectedClient) {
        form.setValue("address", selectedClient.address || "");
        form.setValue("city", selectedClient.city || "");
        form.setValue("state", selectedClient.state || "");
        form.setValue("zip", selectedClient.zip || "");
      }
    }
  }, [watchClientId, clients, form]);

  // Handle form submission
  const onSubmit = async (values: FormValues) => {
    const { date, startTime, endTime, ...rest } = values;
    
    // Use unified date utilities for consistent date handling
    const { startTime: startTimeISO, endTime: endTimeISO } = createEventDateTime(
      date || new Date(),
      startTime || "10:00 AM",
      endTime || "11:00 AM"
    );
    
    const eventData: EventInput = {
      title: values.title,
      description: values.description,
      address: values.address,
      city: values.city,
      state: values.state,
      zip: values.zip,
      type: values.type as "estimate" | "meeting" | "site-visit" | "delivery" | "invoice" | "appointment" | "other",
      status: values.status as "pending" | "confirmed" | "completed" | "cancelled",
      notes: values.notes,
      startTime: startTimeISO,
      endTime: endTimeISO,
      clientId: (values.clientId && values.clientId !== "none" && values.clientId !== "new_client") 
        ? Number(values.clientId) 
        : undefined,
      projectId: (values.projectId && values.projectId !== "none") 
        ? Number(values.projectId) 
        : undefined,
      agentId: (values.agentId && values.agentId !== "none") 
        ? Number(values.agentId) 
        : undefined,
    };
    
    if (eventId) {
      // Update existing event
      await updateEventMutation.mutateAsync({
        id: eventId,
        data: eventData,
      });
    } else {
      // Create new event
      const result = await createEventMutation.mutateAsync(eventData);
      
      // ✅ AUTO-SEND CONFIRMATION EMAIL after creating event
      // Only send if event has a client assigned
      // Don't let email errors break the event creation
      if (result && result.id && eventData.clientId) {
        console.log("📧 Auto-sending confirmation email for new event:", result.id);
        try {
          await sendConfirmationEmail(result.id);
        } catch (emailError) {
          console.error("Email send failed, but event created successfully:", emailError);
          // Show warning toast but don't block success
          toast({
            title: "⚠️ Event Created",
            description: "Event created successfully, but confirmation email failed to send. You can resend it manually.",
            variant: "default",
          });
        }
      }
    }
    
    if (onSuccess) {
      onSuccess();
    }
  };

  console.log("🔍 Client Debug Info:");
  console.log("  - eventData:", eventData);
  console.log("  - currentEvent:", currentEvent);
  console.log("  - currentEvent?.clientId:", currentEvent?.clientId);
  console.log("  - clients:", clients);
  console.log("  - selectedClient:", selectedClient);

  // State for cancellation reason
  const [cancellationReason, setCancellationReason] = useState("");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Function to send confirmation email
  const sendConfirmationEmail = async (eventId: number) => {
    try {
      setIsSendingEmail(true);
      const response = await apiRequest("POST", `/api/protected/events/send-confirmation?id=${eventId}`);
      
      if (response.ok) {
        toast({
          title: "✅ Confirmation Sent",
          description: "Event confirmation email sent to client successfully!",
        });
      } else {
        const error = await response.json();
        throw new Error(error.message || "Failed to send confirmation email");
      }
    } catch (error: any) {
      console.error("Error sending confirmation email:", error);
      toast({
        title: "⚠️ Email Error",
        description: error.message || "Failed to send confirmation email. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Handle cancel/delete event with reason - permanently deletes the event
  const handleCancelEvent = async () => {
    if (!eventId || !cancellationReason.trim()) return;

    try {
      // If event has a client, add cancellation note to client's cancellation history BEFORE deleting
      if (eventData?.clientId) {
        const cancellationEntry = `[${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}] Event "${eventData.title}" cancelled. Reason: ${cancellationReason}`;
        
        // Get current client data to append to existing cancellation history
        const clientRes = await apiRequest("GET", `/api/protected/clients/${eventData.clientId}`);
        const client = await clientRes.json();
        
        const existingHistory = client.cancellationHistory || "";
        const updatedHistory = existingHistory 
          ? `${existingHistory}\n${cancellationEntry}`
          : cancellationEntry;
        
        await apiRequest("PATCH", `/api/protected/clients/${eventData.clientId}`, {
          cancellationHistory: updatedHistory
        });
      }

      // Permanently delete the event from database
      await deleteEventMutation.mutateAsync(eventId);

      setShowCancelDialog(false);
      setCancellationReason("");
      if (onSuccess) onSuccess();
    } catch (error: any) {
      console.error("Error deleting event:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete event. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show loading while event data is being fetched
  if (eventId && isLoadingEvent) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600">Loading event details...</p>
      </div>
    );
  }

  // Show loading while clients/projects/agents are being fetched
  if (isLoadingClients || isLoadingProjects || isLoadingAgents) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <p className="text-sm text-gray-600">Loading form data...</p>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="remodra-card p-6">
        
        {/* Client Information Display - VERY TOP */}
        {eventId && (
          <Card className="remodra-card-container mb-6">
            <CardHeader className="remodra-card-header pb-3">
              <CardTitle className="remodra-card-title flex items-center gap-2 text-amber-400">
                <User className="h-5 w-5 text-amber-400" />
                Client Information
              </CardTitle>
            </CardHeader>
            <CardContent className="remodra-card-content space-y-4">
              {selectedClient ? (
                <div className="space-y-4">
                  {/* Client Name and Contact Info - First Section */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-amber-400" />
                      <span className="remodra-heading-4">
                        {selectedClient.firstName} {selectedClient.lastName}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-7">
                      {selectedClient.email && (
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-amber-400" />
                          <span className="remodra-body-sm">{selectedClient.email}</span>
                        </div>
                      )}
                      {selectedClient.phone && (
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-amber-400" />
                          <span className="remodra-body-sm">{formatPhone(selectedClient.phone)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Address - Second Section */}
                  {selectedClient.address && (
                    <div className="space-y-2 pt-2 remodra-card-footer border-t">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-amber-400 mt-0.5" />
                        <span className="remodra-body-sm">{selectedClient.address}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Notes - Third Section */}
                  {selectedClient.notes && (
                    <div className="remodra-body-sm pt-2 remodra-card-footer border-t">
                      <strong className="text-amber-400">Notes:</strong> {selectedClient.notes}
                    </div>
                  )}
                </div>
              ) : (
                <div className="remodra-body-sm">
                  {eventData?.clientId && (() => {
                    const c = clients.find((x: any) => x.id === eventData.clientId);
                    return c ? <p>Phone: {formatPhone(c.phone) || '—'}</p> : null;
                  })()}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Event Loading Confirmation - show when editing */}
        {eventId && eventData && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
              <div className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full"></div>
              <span className="text-sm font-medium">Event details loaded successfully</span>
            </div>
          </div>
        )}

        {/* Form Loaded Confirmation */}
        {eventId && formLoaded && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
            <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
              <div className="w-2 h-2 bg-green-600 dark:bg-green-400 rounded-full"></div>
              <span className="text-sm font-medium">All form fields populated with event data</span>
            </div>
          </div>
        )}
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="remodra-form-label">Event Title</FormLabel>
              <FormControl>
                <Input 
                  placeholder="e.g.: Site visit for measurements" 
                  {...field} 
                  className="remodra-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="remodra-form-label">Description</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Event details" 
                  {...field} 
                  value={field.value || ""}
                  className="remodra-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className="remodra-form-label">Date</FormLabel>
                <Popover>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant={"outline"}
                        className={cn(
                          "remodra-input w-full pl-3 text-left font-normal",
                          !field.value && "text-slate-400 dark:text-slate-500"
                        )}
                      >
                        {field.value ? (
                          format(field.value, "PPP")
                        ) : (
                          <span>Select a date</span>
                        )}
                        <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={field.value}
                      onSelect={(date) => {
                        field.onChange(date);
                        setDate(date);
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
                          <FormItem>
              <FormLabel className="remodra-form-label">Start Time</FormLabel>
                <FormControl>
                  <TimePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select start time"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="endTime"
            render={({ field }) => (
                          <FormItem>
              <FormLabel className="remodra-form-label">End Time</FormLabel>
                <FormControl>
                  <TimePicker
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Select end time"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="remodra-form-label">Event Type</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="remodra-input">
                      <SelectValue placeholder="Select a type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="site-visit">Site Visit</SelectItem>
                    <SelectItem value="delivery">Material Delivery</SelectItem>
                    <SelectItem value="estimate">Estimate</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="appointment">Appointment</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="status"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="remodra-form-label">Status</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="remodra-input">
                      <SelectValue placeholder="Select a status" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        {/* Client Selection - ABOVE Address */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="clientId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="remodra-form-label">Client</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="remodra-input">
                      <SelectValue placeholder="Select client or meeting type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">No Client (Internal Meeting)</SelectItem>
                    <SelectItem value="new_client">New/Potential Client</SelectItem>
                    {(clients as any[]).map((client: any) => (
                      <SelectItem key={client.id} value={client.id.toString()}>
                        {client.firstName} {client.lastName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
                {field.value === "new_client" && (
                  <p className="text-sm text-blue-400 dark:text-blue-300 mt-1">
                    For meetings with new potential clients. You can create their client record after the meeting.
                  </p>
                )}
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="projectId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="remodra-form-label">Project</FormLabel>
                <Select 
                  onValueChange={field.onChange} 
                  defaultValue={field.value}
                  value={field.value}
                  disabled={!watchClientId}
                >
                  <FormControl>
                    <SelectTrigger className="remodra-input">
                      <SelectValue placeholder={watchClientId ? "Select a project" : "First select a client"} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {filteredProjects.map((project: any) => (
                      <SelectItem key={project.id} value={project.id.toString()}>
                        {project.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {/* Client Information Display - Show when client is selected (ABOVE Address) */}
        {selectedClientForDisplay && (
          <Card className="remodra-card-container">
            <CardContent className="remodra-card-content space-y-3 pt-4">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-amber-400" />
                <span className="remodra-heading-4">
                  {selectedClientForDisplay.firstName} {selectedClientForDisplay.lastName}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-7">
                {selectedClientForDisplay.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-amber-400" />
                    <span className="remodra-body-sm">{selectedClientForDisplay.email}</span>
                  </div>
                )}
                {selectedClientForDisplay.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-amber-400" />
                    <span className="remodra-body-sm">{formatPhone(selectedClientForDisplay.phone)}</span>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Address Fields - BELOW Client Info */}
        <div className="space-y-4">
          <h3 className="remodra-heading-4">Address</h3>
          <FormField
            control={form.control}
            name="address"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="remodra-form-label">Street Address</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Enter street address"
                    {...field}
                    value={field.value || ""}
                    className="remodra-input"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="remodra-form-label">City</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="City"
                      {...field}
                      value={field.value || ""}
                      className="remodra-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="remodra-form-label">State</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="State"
                      {...field}
                      value={field.value || ""}
                      className="remodra-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="zip"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="remodra-form-label">Zip Code</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Zip Code"
                      {...field}
                      value={field.value || ""}
                      className="remodra-input"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>
        
        {/* Agent Assignment */}
        <FormField
          control={form.control}
          name="agentId"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="remodra-form-label">Assign Agent</FormLabel>
              <Select 
                onValueChange={field.onChange} 
                defaultValue={field.value}
                value={field.value}
              >
                <FormControl>
                  <SelectTrigger className="remodra-input">
                    <SelectValue placeholder="Select an agent (optional)" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="none">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-gray-400 border border-white shadow-sm" />
                      <span>No agent assigned</span>
                    </div>
                  </SelectItem>
                  {agents.filter((a: any) => typeof a.id === 'number').map((agent: any) => (
                    <SelectItem key={agent.id} value={agent.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full border border-white shadow-sm flex-shrink-0" 
                          style={{ backgroundColor: agent.colorCode || getAgentColor(agent.id) }}
                        />
                        <span>
                          {agent.firstName} {agent.lastName}
                          {agent.teamRole ? ` (Team: ${agent.teamRole.replace(/_/g, ' ')})` : ` – ${(agent.role || '').replace(/_/g, ' ')}`}
                        </span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        
        <FormField
          control={form.control}
          name="notes"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="remodra-form-label">Additional Notes</FormLabel>
              <FormControl>
                <Textarea 
                  placeholder="Additional notes about the event" 
                  {...field} 
                  value={field.value || ""}
                  className="remodra-input"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        </div>
        
        <div className="flex justify-between pt-2">
          <div className="flex gap-3">
            {eventId && currentEvent?.status !== "cancelled" && (
              <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive" size="sm">
                    <AlertCircle className="mr-2 h-4 w-4" />
                    Cancel Event
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel and Delete Event</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this event from the calendar. Please provide a reason for cancellation. This information will be added to the client's profile for future reference.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="py-4">
                    <Label htmlFor="cancellation-reason">Cancellation Reason</Label>
                    <Textarea
                      id="cancellation-reason"
                      placeholder="e.g., Client requested reschedule, weather conditions, emergency, etc."
                      value={cancellationReason}
                      onChange={(e) => setCancellationReason(e.target.value)}
                      className="remodra-input mt-2"
                      rows={3}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={() => setCancellationReason("")}>
                      Keep Event
                    </AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={handleCancelEvent} 
                      className="bg-red-600 hover:bg-red-700"
                      disabled={!cancellationReason.trim() || deleteEventMutation.isPending}
                    >
                      {deleteEventMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        'Delete Event'
                      )}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          
          <div className="flex gap-3">
            {/* Send Confirmation Email Button - Only show for existing events with clients */}
            {eventId && currentEvent?.clientId && currentEvent?.status !== "cancelled" && (
              <Button 
                type="button"
                variant="outline"
                onClick={() => sendConfirmationEmail(eventId)}
                disabled={isSendingEmail}
                className="remodra-button-outline"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    📧 Send Confirmation
                  </>
                )}
              </Button>
            )}
            
            <Button type="button" variant="outline" onClick={onCancel} className="remodra-button-outline">
              Close
            </Button>
            <Button 
              type="submit"
              disabled={createEventMutation.isPending || updateEventMutation.isPending}
              className="remodra-button"
            >
              {(createEventMutation.isPending || updateEventMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {eventId ? "Update" : "Create"} Event
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}